import React, { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  ChevronRight, 
  ChevronLeft, 
  X, 
  Rocket, 
  BarChart3, 
  FileText, 
  Users, 
  Settings 
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";

interface TourStep {
  targetSelector: string;
  title: string;
  content: string;
  placement: 'top' | 'right' | 'bottom' | 'left';
  icon: React.ElementType;
}

interface FeatureTourProps {
  onComplete: () => void;
  onSkip: () => void;
  isOpen: boolean;
}

export function FeatureTour({ onComplete, onSkip, isOpen }: FeatureTourProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const { toast } = useToast();

  const tourSteps = useMemo(() => [
    {
      targetSelector: "#playbook-feature",
      title: "Growth Playbook",
      content: "Your personalized step-by-step guide to grow your business with proven marketing strategies.",
      placement: "bottom",
      icon: Rocket
    },
    {
      targetSelector: "#analytics-feature",
      title: "Performance Analytics",
      content: "Track your content's performance and see what's working best for your audience.",
      placement: "bottom",
      icon: BarChart3
    },
    {
      targetSelector: "#content-feature",
      title: "Content Creation",
      content: "Generate high-quality content optimized for your target audience with just a few clicks.",
      placement: "bottom",
      icon: FileText
    },
    {
      targetSelector: "#audience-feature",
      title: "Audience Targeting",
      content: "Define and reach your ideal customers with precision content marketing.",
      placement: "bottom",
      icon: Users
    },
    {
      targetSelector: "#settings-feature",
      title: "Easy Configuration",
      content: "Customize your experience to match your business needs and preferences.",
      placement: "left",
      icon: Settings
    }
  ], []);

  useEffect(() => {
    if (!isOpen) return;
    
    const updatePosition = () => {
      // Safety check for current step
      if (currentStep >= tourSteps.length) {
        console.error("Tour step index out of bounds");
        return;
      }
      
      const targetElement = document.querySelector(tourSteps[currentStep]?.targetSelector);
      
      if (!targetElement) {
        console.warn(`Target element '${tourSteps[currentStep]?.targetSelector}' not found in DOM. Using fallback position.`);
        // Use a fallback position in the center of the screen
        const screenWidth = window.innerWidth;
        const screenHeight = window.innerHeight;
        setPosition({ 
          top: screenHeight / 2 - 75,
          left: screenWidth / 2 - 160
        });
        return;
      }
      
      const rect = targetElement.getBoundingClientRect();
      const { placement } = tourSteps[currentStep];
      
      let top = 0;
      let left = 0;
      
      switch (placement) {
        case "top":
          top = rect.top - 10 - 150; // 150px height of tooltip
          left = rect.left + rect.width / 2 - 160; // 320px width of tooltip / 2
          break;
        case "right":
          top = rect.top + rect.height / 2 - 75;
          left = rect.right + 10;
          break;
        case "bottom":
          top = rect.bottom + 10;
          left = rect.left + rect.width / 2 - 160;
          break;
        case "left":
          top = rect.top + rect.height / 2 - 75;
          left = rect.left - 10 - 320;
          break;
      }
      
      // Adjust if off screen
      const screenWidth = window.innerWidth;
      const screenHeight = window.innerHeight;
      
      if (left < 20) left = 20;
      if (left + 320 > screenWidth - 20) left = screenWidth - 340;
      if (top < 20) top = 20;
      if (top + 150 > screenHeight - 20) top = screenHeight - 170;
      
      setPosition({ top, left });
    };
    
    updatePosition();
    
    // Listen for window resize
    window.addEventListener("resize", updatePosition);
    
    // Clean up event listener
    return () => {
      window.removeEventListener("resize", updatePosition);
    };
  }, [currentStep, isOpen, tourSteps]);
  
  const nextStep = () => {
    if (currentStep < tourSteps.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      completeTour();
    }
  };
  
  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };
  
  const completeTour = () => {
    toast({
      title: "Tour Completed!",
      description: "You're all set to start using the platform.",
    });
    onComplete();
  };
  
  if (!isOpen) return null;
  
  const CurrentIcon = tourSteps[currentStep]?.icon;
  
  return (
    <div className="fixed inset-0 z-50 bg-black/30 overflow-hidden" onClick={onSkip}>
      <AnimatePresence mode="wait">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
          style={{ top: position.top, left: position.left }}
          className="fixed z-50 w-80 bg-white dark:bg-gray-900 rounded-lg shadow-xl p-4 cursor-default"
          onClick={e => e.stopPropagation()}
          onError={(error) => {
            console.error("Animation error:", error);
            // Fallback to graceful degradation
            onSkip();
          }}
        >
          <div 
            className={cn(
              "absolute w-3 h-3 bg-white dark:bg-gray-900 transform rotate-45",
              tourSteps[currentStep]?.placement === "top" ? "bottom-0 left-1/2 -translate-x-1/2 translate-y-1.5" :
              tourSteps[currentStep]?.placement === "right" ? "left-0 top-1/2 -translate-y-1/2 -translate-x-1.5" :
              tourSteps[currentStep]?.placement === "bottom" ? "top-0 left-1/2 -translate-x-1/2 -translate-y-1.5" :
              "right-0 top-1/2 -translate-y-1/2 translate-x-1.5"
            )}
          />
          
          <div className="flex justify-between items-center mb-3">
            <div className="flex items-center gap-2">
              <div className="bg-primary/10 p-1.5 rounded-full">
                {CurrentIcon && <CurrentIcon className="h-4 w-4 text-primary" />}
              </div>
              <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                {tourSteps[currentStep]?.title}
              </h3>
            </div>
            <Button 
              size="sm" 
              variant="ghost" 
              className="h-8 w-8 p-0" 
              onClick={onSkip}
            >
              <X className="h-4 w-4" />
              <span className="sr-only">Close</span>
            </Button>
          </div>
          
          <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
            {tourSteps[currentStep]?.content}
          </p>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1">
              {tourSteps.map((_, index) => (
                <div 
                  key={index} 
                  className={cn(
                    "h-1.5 rounded-full transition-all duration-300",
                    index === currentStep ? "w-4 bg-primary" : "w-1.5 bg-gray-200 dark:bg-gray-700"
                  )}
                />
              ))}
            </div>
            
            <div className="flex items-center gap-2">
              <Button 
                size="sm" 
                variant="outline" 
                onClick={prevStep} 
                disabled={currentStep === 0}
                className="h-8"
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Back
              </Button>
              <Button 
                size="sm" 
                onClick={nextStep}
                className="h-8"
              >
                {currentStep === tourSteps.length - 1 ? "Finish" : "Next"}
                {currentStep < tourSteps.length - 1 && <ChevronRight className="h-4 w-4 ml-1" />}
              </Button>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
} 